import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { generatePath, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import {
  EmptyTableState,
  Table,
  TableData,
  TableHead,
} from "~/common/components";
import { isPatientActive, Patient } from "~/common/utils";
import { LoadingPatientsTable } from "~/providers/components/LoadingPatientsTable";
import { ROUTES } from "~/providers/router";
import { patientSearchTableColumns } from "../patientSearchTable/patientSearchTableColumns";
import { usePatientSearchTableSorting } from "../patientSearchTable/usePatientSearchTableSorting";
import { SortableTableHeader } from "../patientsTable/SortableTableHeader";

interface PatientSearchTableProps {
  isFetching?: boolean;
  isLoading?: boolean;
  patients: Patient[];
}

export const PatientSearchTable = ({
  patients,
  isFetching = false,
  isLoading = false,
}: PatientSearchTableProps) => {
  const navigate = useNavigate();
  const { sorting, onSortingChange } = usePatientSearchTableSorting();

  const noPatients = !patients.length;

  const table = useReactTable({
    data: patients,
    columns: patientSearchTableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange,
    state: { sorting },
  });

  const handleRowClick = (patient: Patient) => {
    const tableRowRoute = isPatientActive(
      patient.organization_patients?.[0].status,
    )
      ? generatePath(ROUTES.activePatientActivityFeed, {
          patientId: patient.id,
        })
      : generatePath(ROUTES.consultationPreview, { patientId: patient.id });

    navigate(tableRowRoute);
  };

  return (
    <div
      className={twMerge(
        "w-full overflow-auto rounded-2xl border border-gray-200",
        !isLoading && noPatients && "h-table overflow-clip",
      )}
    >
      <Table className="table-auto">
        <TableHead className="sticky top-0 overflow-hidden">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <SortableTableHeader
                  key={header.id}
                  header={header}
                  className={twMerge(
                    index === 0 && "flex-1 rounded-ss-2xl",
                    index === headerGroup.headers.length - 1 &&
                      "flex-1 rounded-se-2xl",
                    index > 0 &&
                      index < headerGroup.headers.length - 1 &&
                      "flex-1",
                  )}
                />
              ))}
            </tr>
          ))}
        </TableHead>
        {isLoading ? (
          <LoadingPatientsTable length={8} />
        ) : (
          <tbody className="max-h-[38rem] text-sm text-gray-70">
            {noPatients ? (
              <EmptyTableState
                colSpan={patientSearchTableColumns.length}
                message="No patients found"
              />
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={twMerge(
                    "transition hover:cursor-pointer hover:bg-gray-50",
                    isFetching && "opacity-75",
                  )}
                  onClick={() => handleRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableData key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableData>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        )}
      </Table>
    </div>
  );
};
